import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    removeWorkspaceMember,
    setWorkspaceMemberRole,
    subscribeToWorkspaceMembers,
} from 'services/workspaces/WorkspaceMemberService';

const ROLE_OWNER = 'owner';
const ROLE_ADMIN = 'admin';
const ROLE_MEMBER = 'member';

function normalizeRole(value) {
    const role = String(value || '').trim().toLowerCase();
    if (role === ROLE_OWNER || role === ROLE_ADMIN || role === ROLE_MEMBER) return role;
    return ROLE_MEMBER;
}

function roleWeight(role) {
    if (role === ROLE_OWNER) return 0;
    if (role === ROLE_ADMIN) return 1;
    return 2;
}

const useSettingsMembers = ({
    isOpen,
    workspaceKey,
    workspaceRole,
    userKey,
    canChangeRoles,
    canDeleteMembers,
}) => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [statusMessage, setStatusMessage] = useState('');

    useEffect(() => {
        if (!isOpen || !workspaceKey) {
            setMembers([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const unsubscribe = subscribeToWorkspaceMembers(
            workspaceKey,
            (rows) => {
                setMembers(Array.isArray(rows) ? rows : []);
                setLoading(false);
            },
            (error) => {
                setErrorMessage(error?.message || 'Failed to load members.');
                setLoading(false);
            }
        );

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [isOpen, workspaceKey]);

    const sortedMembers = useMemo(() => {
        return [...members].sort((a, b) => {
            const roleDelta = roleWeight(normalizeRole(a?.role)) - roleWeight(normalizeRole(b?.role));
            if (roleDelta !== 0) return roleDelta;

            const aName = String(a?.title || a?.email || a?.userKey || '').toLowerCase();
            const bName = String(b?.title || b?.email || b?.userKey || '').toLowerCase();
            return aName.localeCompare(bName);
        });
    }, [members]);

    const subtitle = loading
        ? 'Loading members...'
        : `${members.length} member${members.length === 1 ? '' : 's'}`;

    const canManageTarget = useCallback((member, action) => {
        const targetUserKey = member?.userKey || member?.key;
        const targetRole = normalizeRole(member?.role);
        const allowedByAction = action === 'changeRole' ? canChangeRoles : canDeleteMembers;

        if (!allowedByAction) return false;
        if (!targetUserKey || targetUserKey === userKey) return false;
        if (targetRole === ROLE_OWNER) return false;

        if (workspaceRole === ROLE_ADMIN && targetRole === ROLE_ADMIN) {
            return false;
        }

        return true;
    }, [canChangeRoles, canDeleteMembers, userKey, workspaceRole]);

    const handleRoleChange = useCallback(async (member, nextRole) => {
        if (!workspaceKey || !canManageTarget(member, 'changeRole')) return;

        const targetUserKey = member?.userKey || member?.key;
        const role = normalizeRole(nextRole);
        if (!targetUserKey || role === ROLE_OWNER) return;

        try {
            setErrorMessage('');
            await setWorkspaceMemberRole(workspaceKey, targetUserKey, role);
            setStatusMessage('Role updated.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to update role.');
        }
    }, [canManageTarget, workspaceKey]);

    const handleRemoveMember = useCallback(async (member) => {
        if (!workspaceKey || !canManageTarget(member, 'remove')) return;

        const targetUserKey = member?.userKey || member?.key;
        if (!targetUserKey) return;

        try {
            setErrorMessage('');
            await removeWorkspaceMember(workspaceKey, targetUserKey);
            setStatusMessage('Member removed.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to remove member.');
        }
    }, [canManageTarget, workspaceKey]);

    const clearMessages = useCallback(() => {
        setErrorMessage('');
        setStatusMessage('');
    }, []);

    return {
        members,
        sortedMembers,
        subtitle,
        loading,
        errorMessage,
        statusMessage,
        clearMessages,
        canManageTarget,
        handleRoleChange,
        handleRemoveMember,
    };
};

export default useSettingsMembers;
